/** Inline WYSIWYG config editor — renders a small pencil icon admin-only.
 *  Clicking opens a popover with toggles affecting just that card's metrics.
 *  Edits PUT to /api/clients/:rootId/report-config; on save we re-fetch the
 *  dashboard so the live view reflects the change without a page reload.
 */
import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { PencilSimple, Check } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/** A single boolean toggle inside the popover. */
function ToggleRow({ label, sub, value, onChange, disabled, badge }) {
  return (
    <div className={`flex items-start justify-between gap-3 py-2 ${disabled ? "opacity-50" : ""}`}>
      <div className="min-w-0">
        <p className="text-xs text-[#0F172A] font-medium flex items-center gap-1.5">
          {label}
          {badge && (
            <span className="text-[11px] uppercase tracking-[0.18em] font-mono bg-[#EEEEEE] text-[#475569] px-1.5 py-0.5 rounded">
              {badge}
            </span>
          )}
        </p>
        {sub && <p className="text-[11px] text-[#94A3B8] mt-0.5 leading-snug">{sub}</p>}
      </div>
      <Switch checked={!!value} onCheckedChange={onChange} disabled={disabled} />
    </div>
  );
}

export default function FlashConfigEditor({
  clientId,           // root_client_id (NOT the magic token)
  config,             // resolved config from dash.report_config
  scope,              // "kpi" | "splits" | "suppliers" | "charts"
  testid,
  onSaved,            // () => void — typically triggers a dashboard refetch
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const onOpen = (next) => {
    setOpen(next);
    if (next) setDraft(JSON.parse(JSON.stringify(config?.flash || {})));
    else setDraft(null);
  };

  if (!clientId || !config) return null;

  const set = (path, val) => {
    setDraft((d) => {
      const copy = JSON.parse(JSON.stringify(d || {}));
      const segs = path.split(".");
      let cur = copy;
      for (let i = 0; i < segs.length - 1; i++) {
        cur[segs[i]] = cur[segs[i]] || {};
        cur = cur[segs[i]];
      }
      cur[segs[segs.length - 1]] = val;
      return copy;
    });
  };

  const v = (path) => {
    const segs = path.split(".");
    let cur = draft || {};
    for (const s of segs) { if (!cur) return undefined; cur = cur[s]; }
    return cur;
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/clients/${clientId}/report-config`, { flash: draft });
      toast.success("Report settings saved");
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid || `flash-edit-${scope}`}
          className="inline-flex items-center justify-center w-6 h-6 text-[#94A3B8] hover:text-[#0F172A] hover:bg-[#EEEEEE] rounded-md transition-colors"
          aria-label="Edit this card"
        >
          <PencilSimple size={14} weight="regular" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-4 rounded-md border-[#E2E8F0]"
        data-testid={`${testid || `flash-edit-${scope}`}-popover`}
      >
        <p className="uppercase text-[11px] tracking-[0.2em] text-[#475569] font-medium mb-3">
          Edit · {scope}
        </p>
        <div className="divide-y divide-[#EEEEEE] -my-1">
          {scope === "kpi" && (
            <>
              <ToggleRow
                label="Show Gross Profit £"
                sub="Large GP £ readout below the GP% headline."
                value={v("kpi.emphasise_gp_money")}
                onChange={(b) => set("kpi.emphasise_gp_money", b)}
              />
            </>
          )}
          {scope === "charts" && (
            <>
              <ToggleRow
                label="Cumulative line"
                sub="Overlay a running-total line on top of the daily bars."
                value={v("charts.show_cumulative")}
                onChange={(b) => set("charts.show_cumulative", b)}
              />
              <ToggleRow
                label="Budget overlay"
                sub="Plot the Xero budget alongside the daily bars."
                value={v("charts.show_budget")}
                onChange={(b) => set("charts.show_budget", b)}
                disabled
                badge="coming soon"
              />
              <ToggleRow
                label="STLY in hover tooltip"
                sub="Show same-time-last-year value and £/% variance on hover."
                value={v("charts.show_stly_in_tooltip")}
                onChange={(b) => set("charts.show_stly_in_tooltip", b)}
              />
            </>
          )}
          {scope === "splits" && (
            <>
              <ToggleRow
                label="Gross Profit £ column"
                sub="Adds a 4th metric inside the Food and Beverage cards."
                value={v("splits.show_gp_money")}
                onChange={(b) => set("splits.show_gp_money", b)}
              />
            </>
          )}
          {scope === "suppliers" && (
            <>
              <ToggleRow
                label="% of sales next to spend"
                sub="Adds spend-as-% of net-sales beside each supplier total."
                value={v("suppliers.show_pct_of_sales")}
                onChange={(b) => set("suppliers.show_pct_of_sales", b)}
              />
            </>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-[#EEEEEE]">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 text-xs"
            onClick={() => setOpen(false)}
            data-testid={`${testid || `flash-edit-${scope}`}-cancel`}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={saving}
            onClick={save}
            data-testid={`${testid || `flash-edit-${scope}`}-save`}
            className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-md h-8 text-xs"
          >
            <Check size={11} weight="bold" className="mr-1" /> {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
